<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { presentationsApi } from '../api'
import AppShell from '../components/AppShell.vue'
import ErrorState from '../components/ErrorState.vue'
import LoadingState from '../components/LoadingState.vue'
import SlidePreview from '../components/SlidePreview.vue'
import { navigate } from '../router'
import { themeName } from '../themeData'
import type { Presentation } from '../types'

defineEmits<{
  toggleTheme: []
}>()

const items = ref<Presentation[]>([])
const loading = ref(true)
const error = ref('')

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const response = await presentationsApi.list()
    items.value = response.items
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Не удалось загрузить презентации.'
  } finally {
    loading.value = false
  }
}

function openDeck(id: string): void {
  navigate(`/deck/${id}`)
}

function relativeDate(value: string): string {
  const date = new Date(value)
  const delta = Date.now() - date.getTime()
  if (delta < 60_000) return 'только что'
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)} мин назад`
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)} ч назад`
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

onMounted(load)
</script>

<template>
  <AppShell @toggle-theme="$emit('toggleTheme')">
    <section class="page-heading">
      <div>
        <span class="eyebrow">Рабочее пространство</span>
        <h1>Мои презентации</h1>
        <p>Здесь живут черновики и готовые выступления.</p>
      </div>
      <a href="/new" class="button button--primary">
        <span aria-hidden="true">+</span>
        Новая презентация
      </a>
    </section>

    <LoadingState v-if="loading" title="Собираем ваши презентации" />
    <ErrorState v-else-if="error" :text="error" @retry="load" />

    <section v-else-if="items.length" class="deck-grid" aria-label="Презентации">
      <button
        v-for="deck in items"
        :key="deck.id"
        type="button"
        class="deck-card"
        @click="openDeck(deck.id)"
      >
        <SlidePreview
          :src="deck.previewUrl ?? null"
          :aspect="deck.aspect"
          :alt="`Первый слайд презентации «${deck.title}»`"
        >
          <span class="deck-card__status" :class="`status--${deck.status}`">
            {{ deck.publishedRevisionId ? 'Опубликована' : deck.status === 'failed' ? 'Нужна помощь' : 'Черновик' }}
          </span>
        </SlidePreview>
        <span class="deck-card__body">
          <strong>{{ deck.title }}</strong>
          <span>
            {{ themeName(deck.themeId) }} · {{ deck.aspect }} · {{ relativeDate(deck.updatedAt) }}
          </span>
        </span>
      </button>

      <a href="/new" class="deck-card deck-card--new">
        <span class="deck-card__plus">+</span>
        <strong>Начать новую</strong>
        <small>Достаточно пары предложений</small>
      </a>
    </section>

    <section v-else class="empty-library">
      <div class="empty-library__art" aria-hidden="true">
        <span></span><span></span><span></span>
      </div>
      <span class="eyebrow">Чистый лист — это ненадолго</span>
      <h2>Первая презентация начинается здесь</h2>
      <p>Опишите тему своими словами. GetSlides соберёт структуру, текст и оформление.</p>
      <a href="/new" class="button button--primary button--large">
        Сделать презентацию
      </a>
    </section>
  </AppShell>
</template>
